import * as signalR from '@microsoft/signalr';
import store from '@/store';
import { apiConfig } from '@/utils/config';

class SignalRService {
  constructor() {
    this.connection = null;
    this.reconnectInterval = 5000; // Time between reconnection attempts in milliseconds
    this.eventHandlers = {}; // Store event handlers
    this.requestQueue = []; // Queue for requests made before connection
    this.pingInterval = 10000;
    this.pingMonitor = null;
    this.pingTimeout = 3000;
  }

  async initializeConnection() {
    if (!this.isAuthenticated()) {
      console.warn("Cannot initialize connection: User is not authenticated.");
      return;
    }

    this.createConnection();

    try {
      this.stopPingMonitor();

      await this.connection.start();

      console.log("SignalR connection established");
      this.reSubscribeToEvents();
      this.processQueue(); // Process queued requests
      this.isReconnecting = false;
      this.startPingMonitor();

    } catch (error) {
      console.error("SignalR connection error:", error);
      this.handleReconnection();
    }

    this.connection.onclose(() => {
      console.log('SignalR connection closed.');
      this.handleReconnection();
    });

    this.connection.onreconnected(() => {
      console.log('SignalR connection reestablished.');
      this.reSubscribeToEvents();
      this.processQueue(); // Process queued requests
    });
  }


  createConnection() {
    const currentUser = store.getters['auth/token'];
    if (!currentUser) return;

    const accessToken = currentUser;
    const baseURL = apiConfig.baseUrl.replace('/v1/', '');
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${baseURL}/notificationHub`, {
        accessTokenFactory: () => accessToken,
      })
      .build();
  }

  handleReconnection() {
    if (this.isAuthenticated()) {
      setTimeout(() => {
        this.isReconnecting = true;
        this.createConnection();
        this.initializeConnection();
      }, this.reconnectInterval);
    }
  }

  isAuthenticated() {
    const currentUser = store.getters['auth/currentUser'];
    return currentUser;
  }

  async getPaginatedUsers(pageNumber, pageSize) {
    if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
      try {
        return await this.connection.invoke('GetPaginatedUsers', pageNumber, pageSize);
      } catch (error) {
        console.error('Error while fetching paginated users:', error);
        return { users: [], totalCount: 0 };
      }
    } else {
      console.warn('Connection not established. Queuing request.');
      return new Promise((resolve) => {
        this.requestQueue.push(() => {
          this.getPaginatedUsers(pageNumber, pageSize).then(resolve);
        });
      });
    }
  }

  startPingMonitor() {
    // Clear any existing monitor first
    this.stopPingMonitor();

    console.log('Starting ping monitor...');
    this.lastPingResponse = Date.now();
    this.isConnectionHealthy = true;

    this.pingMonitor = setInterval(async () => {
      await this.performHealthCheck();
    }, this.pingInterval);
  }

  stopPingMonitor() {
    if (this.pingMonitor) {
      clearInterval(this.pingMonitor);
      this.pingMonitor = null;
      this.handlePingResponse();//reset counter and lastsuccessful
      console.log('Ping monitor stopped');
    }
  }

  async performHealthCheck() {
    // Don't check health if we're already reconnecting
    if (this.isReconnecting) {
      console.log('is reconnecting... returning...');
      return;
    }

    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      console.warn('Connection not in connected state, skipping health check');
      return;
    }

    const timeSinceLastPing = Date.now() - this.lastPingResponse;

    // If we haven't received a ping response in 2 intervals, consider connection dead
    if (timeSinceLastPing > this.pingInterval * 5) {
      console.warn('No ping response received for too long, connection may be zombie');
      await this.handleZombieConnection();
      return;
    }

    // Send new ping with timeout
    try {
      await this.sendPingWithTimeout();
    } catch (error) {
      console.error('Ping failed:', error.message);
      // Don't immediately trigger reconnection on single ping failure
      // Wait for the next health check to confirm
    }
  }

  async sendPingWithTimeout() {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error('Connection not available for ping');
    }
    let timeoutId;
    try {
      const pingPromise = (async () => {
        const result = await this.connection.invoke("Ping");
        return result;
      })();

      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error(`Ping timeout after ${this.pingTimeout}ms`));
        }, this.pingTimeout);
      });

      // Race them immediately
      const result = await Promise.race([pingPromise, timeoutPromise]);

      this.handlePingResponse();
      return result;

    } catch (error) {
      // This will catch both ping errors and timeout errors
      console.warn(`Ping attempt failed: ${error.message}`);
      throw error;
    }
    finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  }

  handlePingResponse() {
    this.lastPingResponse = Date.now();
    this.isConnectionHealthy = true;
    this.reconnectAttempts = 0; // Reset attempts on successful ping
  }

  async handleZombieConnection() {
    // Prevent multiple simultaneous zombie recovery attempts
    if (this.isReconnecting) {
      console.log('Already reconnecting, skipping duplicate zombie recovery');
      return;
    }

    console.warn('Zombie connection detected, forcing reconnection...');
    this.isConnectionHealthy = false;
    this.isReconnecting = true;
    this.stopPingMonitor();

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached, giving up');
      this.reconnectAttempts = 0;
      this.isReconnecting = false;
      return;
    }

    this.reconnectAttempts++;

    try {

      const stopPromise = this.connection.stop();
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Stop operation timed out')), 5000);
      });

      // Race between stop operation and timeout
      await Promise.race([stopPromise, timeoutPromise]);
    } catch (error) {
      console.error('Error during zombie connection recovery:', error);
      this.isReconnecting = false;
      this.handleReconnection();
    }
  }

  async testPing() {
    try {
      console.log('Manual ping test started...');
      await this.sendPingWithTimeout();
      console.log('Manual ping test: SUCCESS');
      return { success: true, message: 'Ping successful' };
    } catch (error) {
      console.log('Manual ping test: FAILED -', error.message);
      return { success: false, message: error.message };
    }
  }

  processQueue() {
    while (this.requestQueue.length > 0) {
      const request = this.requestQueue.shift();
      request();
    }
  }

  subscribeToEvent(eventName, callback) {
    if (this.connection) {
      if (!this.eventHandlers[eventName]) {
        this.eventHandlers[eventName] = [];
      }
      this.eventHandlers[eventName].push(callback);
      this.connection.on(eventName, callback);
    } else {
      console.error('Connection is not established.');
    }
  }

  reSubscribeToEvents() {
    for (const [eventName, handlers] of Object.entries(this.eventHandlers)) {
      handlers.forEach(handler => {
        this.connection.on(eventName, handler);
      });
    }
  }
}

export default new SignalRService();
